export const RUN_STATUSES = ['processing', 'completed', 'rejected', 'ingest_failed', 'failed', 'timed_out'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const TERMINAL_RUN_STATUSES = RUN_STATUSES.filter(s => s !== 'processing');
export const DATASET_STATUSES = [
  'uploaded',
  'pending_classification',
  'ingested',
  'invalid',
  'evaluating',
  'review_pending',
  'awaiting_vendor_input',
  'completed',
  'failed'
] as const;
/**
 * Gate is a QA fact; lifecycle is a workflow state, and the two are kept apart deliberately.
 *
 * Every settled run lands in `decision_required` whatever its gate said — that is the operator's
 * queue, and `docs/10-domain/domain-model.md` has specified it from the start. The gate is shown
 * beside the stage as its own badge; encoding it a second time in the stage is what produced the
 * previous vocabulary, where `evaluated` meant both "QA passed, nobody has looked" and "an admin
 * approved this", and a failed gate skipped the operator entirely.
 *
 * `approved` and `rejected` say a human acted and what they said. `needs_fixes` goes back to meaning
 * only what the *vendor* must fix before anyone can judge it: a blocking flag, or a file rejected at
 * intake.
 *
 * `evaluated`, `in_review` and `results` are retired; migration 010 maps the stored rows across and
 * 011 removes them from the CHECK. They stay in the union until then so a process on the old build
 * can still render a row it wrote.
 */
export const DATASET_LIFECYCLE_STATUSES = [
  'awaiting_upload',
  'uploading',
  'ready_for_evaluation',
  'evaluating',
  'decision_required',
  'approved',
  'rejected',
  'needs_fixes',
  'certified',
  'awaiting_vendor_input',
  'upload_failed',
  /** @deprecated retired by migration 011. */
  'in_review',
  /** @deprecated retired by migration 011. */
  'evaluated',
  /** @deprecated retired by migration 011. */
  'results'
] as const;
export type DatasetLifecycleStatus = (typeof DATASET_LIFECYCLE_STATUSES)[number];
export const SOURCE_KINDS = ['archive', 'directory', 'loose'] as const;

/**
 * How each lifecycle status renders. This lives here, beside the status list itself, because the
 * client previously kept its own copy that knew only eight of the twelve: `certified`,
 * `needs_fixes`, `in_review` and `evaluated` all fell through to a grey badge with an
 * underscore-stripped label, so a certified dataset looked unfinished and a blocked one looked
 * idle. Typing it as a total Record over the status union means a new status cannot be added
 * without deciding how it reads.
 *
 * `tone` is the semantic state, not a colour: the theme maps it.
 */
export type LifecycleTone = 'pass' | 'warn' | 'danger' | 'muted';

/**
 * One stored state, read differently by the two people looking at it.
 *
 * A dataset waiting on an operator is *their queue* and *the vendor's news*: "Decision required" is
 * meaningless to a vendor who cannot act on it, and "Evaluated" tells an operator nothing about what
 * they owe. `vendorLabel` is the second reading; where it is absent both realms say the same thing.
 *
 * `tone` is the semantic state, not a colour: the theme maps it.
 */
export const LIFECYCLE_DISPLAY: Record<
  DatasetLifecycleStatus,
  { label: string; tone: LifecycleTone; vendorLabel?: string; vendorTone?: LifecycleTone }
> = {
  awaiting_upload: { label: 'Awaiting upload', tone: 'muted' },
  uploading: { label: 'Uploading', tone: 'muted' },
  ready_for_evaluation: { label: 'Ready for evaluation', tone: 'muted' },
  evaluating: { label: 'In evaluation', tone: 'warn' },
  /**
   * The operator's queue, and the vendor's wait.
   *
   * "Decision required" is meaningless to a vendor who cannot act on it, and "Evaluated" reads as
   * *finished* — the machine's part is done, but nothing has been decided, and a vendor told
   * "Evaluated" reasonably expects a verdict and a report. "Under review" says the true thing: the
   * evaluation ran, and a person is now looking at it.
   */
  decision_required: { label: 'Decision required', tone: 'warn', vendorLabel: 'Under review' },
  approved: { label: 'Approved', tone: 'pass' },
  // An operator judged it and said no. For the vendor that is not news, it is work.
  rejected: { label: 'Rejected', tone: 'danger', vendorLabel: 'Needs your action' },
  // Something was wrong before anyone could judge it: a blocking flag, or a file rejected at intake.
  needs_fixes: { label: 'Needs fixes', tone: 'danger' },
  certified: { label: 'Certified', tone: 'pass' },
  awaiting_vendor_input: { label: 'Awaiting input', tone: 'danger' },
  upload_failed: { label: 'Upload failed', tone: 'danger' },
  /** @deprecated Retired by migration 011; kept so a row written before it still renders. */
  in_review: { label: 'Decision required', tone: 'warn', vendorLabel: 'Under review' },
  /** @deprecated Retired by migration 011. */
  evaluated: { label: 'Decision required', tone: 'warn', vendorLabel: 'Under review' },
  /** @deprecated Retired by migration 011. */
  results: { label: 'Approved', tone: 'pass' }
};

/**
 * Dataset vocabulary. These live here rather than in apps/web because both sides now depend on the
 * same list: the API validates against it on intake and the client renders chips from it. A second
 * copy is a second thing to forget to extend.
 */
export const MODALITIES = ['ego', 'rgb_d', 'audio', 'teleop', 'multi_rgb', 'depth', 'force_torque'] as const;
export type Modality = (typeof MODALITIES)[number];
export const COLLECTION_TYPES = ['in_the_wild', 'human_demo', 'teleoperation', 'scripted'] as const;
export type CollectionType = (typeof COLLECTION_TYPES)[number];
export const AVAILABILITIES = ['available', 'reserved', 'delivered'] as const;
export type Availability = (typeof AVAILABILITIES)[number];
export const INTAKE_METHODS = ['manifest', 'browser'] as const;
export type IntakeMethod = (typeof INTAKE_METHODS)[number];
/** null means not yet evaluated, and is rendered as an em dash rather than as a failure. */
export const GATES = ['pass', 'cond', 'fail'] as const;
export type Gate = (typeof GATES)[number] | null;
export const TIERS = ['A', 'B', 'C'] as const;
export type Tier = (typeof TIERS)[number] | null;

/**
 * The five quality blocks, in fixed display order. The order IS the design: the mockups number them
 * 1..5 and render a fixed-width bar, so a missing block is returned as `unevaluated` and never
 * omitted -- each position always means the same dimension.
 *
 * The vocabulary is ours, not the QA vendor's. An unknown key from a vendor report is dropped; a
 * missing one is materialised. That is what keeps the bar stable across a change of QA provider.
 */
export const QUALITY_BLOCKS = [
  { key: 'core_structural', label: 'Core structural' },
  { key: 'modality_overlays', label: 'Modality overlays' },
  { key: 'high_stakes_governance', label: 'High-stakes governance' },
  { key: 'payload_qa_readiness', label: 'Payload QA readiness' },
  { key: 'fitness', label: 'Fitness' }
] as const;
export type QualityBlockKey = (typeof QUALITY_BLOCKS)[number]['key'];
export const BLOCK_VERDICTS = ['pass', 'warn', 'fail', 'unevaluated', 'not_applicable'] as const;
export type BlockVerdict = (typeof BLOCK_VERDICTS)[number];

export interface MaterialisedQualityBlock {
  key: QualityBlockKey;
  verdict: BlockVerdict;
  detail: string | null;
  checks: unknown[];
}

/**
 * The five blocks, always all five, always in display order.
 *
 * This rule was implemented three times -- the API's write path, the API's read path, and the
 * client's normaliser -- under a comment in one of them claiming "there is exactly one derivation
 * and it lives here". Each had to independently get the same things right: a missing block is
 * `unevaluated` rather than absent, an unrecognised verdict is not trusted, and position carries
 * meaning because the bar is fixed-width and numbered 1..5.
 *
 * `onUnknownKey` reports a key the vendor sent that is not ours. Dropping it is what keeps the bar
 * stable across a change of QA provider; hearing about it is what stops that being silent.
 */
export function materialiseQualityBlocks(
  supplied: unknown,
  onUnknownKey?: (key: string) => void
): MaterialisedQualityBlock[] {
  const byKey = new Map<string, any>((Array.isArray(supplied) ? supplied : []).map((b: any) => [String(b?.key), b]));
  if (onUnknownKey) for (const key of byKey.keys()) if (!QUALITY_BLOCKS.some(b => b.key === key)) onUnknownKey(key);
  return QUALITY_BLOCKS.map(({ key }) => {
    const b = byKey.get(key);
    return {
      key,
      verdict: (BLOCK_VERDICTS as readonly string[]).includes(b?.verdict) ? (b.verdict as BlockVerdict) : 'unevaluated',
      detail: typeof b?.detail === 'string' ? b.detail : null,
      checks: Array.isArray(b?.checks) ? b.checks : []
    };
  });
}

/** Organisation-level vendor status. Distinct from users.onboarding_status, which is an auth fact. */
export const VENDOR_ONBOARDING_STATUSES = ['invited', 'registered', 'trial_batch', 'active', 'suspended'] as const;
export type VendorOnboardingStatus = (typeof VENDOR_ONBOARDING_STATUSES)[number];
export const ROLES = ['super_admin', 'admin', 'viewer'] as const;
export const PERMISSIONS = [
  'admin.management.view',
  'admin.management.create',
  'admin.management.edit',
  'admin.management.deactivate',
  'admin.management.mfa_reset',
  'vendor.view',
  'vendor.create',
  'vendor.manage',
  'vendor.mfa_reset',
  'dataset.view',
  'dataset.delete',
  'dataset.evaluate',
  'dataset.public_marking',
  'dataset.tags',
  'tenant.view',
  'tenant.manage',
  'verification.view',
  'verification.decide',
  'verification.override',
  'audit.view',
  'dashboard.view',
  'realtime.connect'
] as const;
export type Permission = (typeof PERMISSIONS)[number];
export type OperatorRole = (typeof ROLES)[number];
export const ROLE_PERMISSIONS: Record<OperatorRole, readonly Permission[]> = {
  super_admin: PERMISSIONS,
  admin: PERMISSIONS.filter(
    p => !p.startsWith('admin.management.') && p !== 'verification.override' && p !== 'dataset.delete'
  ),
  viewer: [
    'vendor.view',
    'dataset.view',
    'tenant.view',
    'verification.view',
    'audit.view',
    'dashboard.view',
    'realtime.connect'
  ]
};
/**
 * `EventEnvelope` lives in ./events now, keyed by event type so the payload follows from it.
 *
 * The version removed here took the payload as its parameter and was used by nothing. It also
 * described an envelope the system does not produce -- `eventVersion`, `envelopeVersion` and
 * `producer` are not fields `OutboxService.emit` writes -- so it was a contract that agreed with
 * no producer and no consumer, quietly shadowing the real one through `export *`.
 */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  code: string;
  traceId: string;
  errors?: Record<string, string[]>;
}
export interface CursorPage<T> {
  data: T[];
  next_cursor: string | null;
}
export interface QaClientResult {
  successful: boolean;
  statusCode: number | null;
  failureReason: string | null;
  rawBody: string;
  received: boolean;
  jobId?: string;
}

/**
 * A submit is not a poll, and the difference is whether a job id exists.
 *
 * `QaClientResult` said `jobId?: string` for both, so the caller had to remember that a successful
 * submit always carries one -- and the poll tick it went on to schedule was typed as though it
 * might not. Discriminating on `successful` says it once, here.
 */
export type QaSubmitResult =
  | (Omit<QaClientResult, 'jobId'> & { successful: true; jobId: string })
  | (Omit<QaClientResult, 'jobId'> & { successful: false; jobId?: undefined });

/** A run that has settled. `processing` is excluded because settling is what ends it (INV-16). */
export type TerminalRunStatus = Exclude<RunStatus, 'processing'>;

export * from './annotations';
export * from './events';
export * from './schemas';
